import i18next from "i18next"
import { EPaymentType } from "../../Enum/EPaymentType"
import { EManualExternalOrderStatus } from "./EManualExternalOrderStatus"
import { EManualExternalOrderReconciliationStatus } from "./EManualExternalOrderReconciliationStatus"
import { EManualExternalOrderDeliveryType } from "./EManualExternalOrderDeliveryType"
import { EManualExternalOrderSourceType } from "./EManualExternalOrderSourceType"
import { ManualExternalOrder } from "./ManualExternalOrder"
import { currentDay } from "../Generics/Helps"

export const enumOptions = <T extends Record<string, number>>(source: T) =>
  Object.values(source).filter((value): value is T[keyof T] => typeof value === "number")

export const createManualExternalOrderDropdownOptions = (
  values: number[],
  labelResolver: (value: number) => string,
  labelAll?: string,
) => {
  const options = values.map((value) => ({ label: labelResolver(value), value: value.toString() }))

  if (!labelAll) {
    return options
  }

  return [{ label: labelAll, value: "" }, ...options]
}

export const getManualExternalOrderDropdownValue = (value?: number | "") =>
  value === undefined || value === "" ? "" : value.toString()

export const getManualExternalOrderSourceLabel = (value: number) =>
  i18next.t(`manualExternalOrder:source.${value}`, { defaultValue: value.toString() })

export const getManualExternalOrderStatusLabel = (value: number) =>
  i18next.t(`manualExternalOrder:status.${value}`, { defaultValue: value.toString() })

export const getManualExternalOrderReconciliationLabel = (value: number) =>
  i18next.t(`manualExternalOrder:reconciliation.${value}`, { defaultValue: value.toString() })

export const getManualExternalOrderDeliveryLabel = (value: number) =>
  i18next.t(`manualExternalOrder:delivery.${value}`, { defaultValue: value.toString() })

export const getManualExternalOrderPaymentLabel = (value: number) =>
  i18next.t(`manualExternalOrder:payment.${value}`, { defaultValue: value.toString() })

export const getManualExternalOrderStatusClassName = (value: number) => {
  switch (value) {
    case EManualExternalOrderStatus.Cancelled:
    case EManualExternalOrderStatus.Refunded:
      return "bg-red-100 text-red-700"
    case EManualExternalOrderStatus.Received:
    case EManualExternalOrderStatus.Reconciled:
      return "bg-green-100 text-green-700"
    case EManualExternalOrderStatus.Confirmed:
      return "bg-blue-100 text-blue-700"
    default:
      return "bg-amber-100 text-amber-700"
  }
}

export const createEmptyManualExternalOrder = (): ManualExternalOrder => ({
  id: 0,
  companyId: 0,
  sourceType: EManualExternalOrderSourceType.Other,
  orderDate: currentDay(),
  deliveryType: EManualExternalOrderDeliveryType.None,
  paymentMethod: EPaymentType.Pix,
  productsAmount: 0,
  additionsAmount: 0,
  deliveryFeeAmount: 0,
  discountAmount: 0,
  grossAmount: 0,
  platformFeeAmount: 0,
  paymentFeeAmount: 0,
  otherCostsAmount: 0,
  netAmount: 0,
  status: EManualExternalOrderStatus.Pending,
  reconciliationStatus: EManualExternalOrderReconciliationStatus.NotReconciled,
  items: [],
})

export const previewManualExternalOrderTotals = (order: ManualExternalOrder) => {
  const productsAmount =
    order.productsAmount > 0
      ? order.productsAmount
      : order.items.reduce((sum, item) => sum + (item.totalPrice ?? item.quantity * item.unitPrice), 0)

  const hasDetailedGross =
    productsAmount > 0 || order.additionsAmount > 0 || order.deliveryFeeAmount > 0 || order.discountAmount > 0
  const grossAmount = hasDetailedGross
    ? Math.max(0, productsAmount + order.additionsAmount + order.deliveryFeeAmount - order.discountAmount)
    : Math.max(0, order.grossAmount)
  const hasCosts = order.platformFeeAmount > 0 || order.paymentFeeAmount > 0 || order.otherCostsAmount > 0
  const netAmount =
    hasCosts || hasDetailedGross
      ? Math.max(0, grossAmount - order.platformFeeAmount - order.paymentFeeAmount - order.otherCostsAmount)
      : Math.max(0, order.netAmount || grossAmount)

  return { productsAmount, grossAmount, netAmount }
}

export const getManualExternalOrderReconciliationClassName = (value: number) => {
  switch (value) {
    case EManualExternalOrderReconciliationStatus.Reconciled:
      return "bg-green-100 text-green-700"
    case EManualExternalOrderReconciliationStatus.Ignored:
      return "bg-slate-100 text-slate-700"
    case EManualExternalOrderReconciliationStatus.ReceivedWithDifference:
      return "bg-red-100 text-red-700"
    default:
      return "bg-amber-100 text-amber-700"
  }
}

export const manualExternalOrderSourceOptions = [
  ...enumOptions(EManualExternalOrderSourceType).filter((value) => value !== EManualExternalOrderSourceType.Other),
  EManualExternalOrderSourceType.Other,
]

export const manualExternalOrderStatusOptions = enumOptions(EManualExternalOrderStatus)

export const manualExternalOrderReconciliationOptions = enumOptions(EManualExternalOrderReconciliationStatus)

export const manualExternalOrderDeliveryOptions = enumOptions(EManualExternalOrderDeliveryType)

export const manualExternalOrderPaymentOptions = enumOptions(EPaymentType)
